import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LifeBuoy,
  ShieldAlert,
  Filter,
  Send,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
  MailCheck,
  RefreshCw,
} from "lucide-react";

// ============================================================================
// AdminSupport — internal founder-team console for the Help-Desk Support Vault.
// Server-side gated by ADMIN_EMAILS allowlist (_require_internal_admin).
// Non-admin users are shown a red allowlist warning card.
// ============================================================================
const STATUS_OPTIONS = ["all", "open", "in_progress", "closed"];
const CATEGORY_COLOR = {
  bug_report: "border-red-500/40 text-red-200 bg-red-500/8",
  billing: "border-emerald-500/40 text-emerald-200 bg-emerald-500/8",
  feature_guide: "border-cyan-500/40 text-cyan-200 bg-cyan-500/8",
  other: "border-slate-500/40 text-slate-200 bg-slate-500/8",
};

function StatusPill({ status }) {
  const map = {
    open:        { icon: Clock, color: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
    in_progress: { icon: RefreshCw, color: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10" },
    closed:      { icon: CheckCircle2, color: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  }[status] || { icon: Clock, color: "text-slate-300 border-slate-500/40 bg-slate-500/10" };
  const Icon = map.icon;
  return (
    <span
      data-testid={`ticket-status-${status}`}
      className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-md border ${map.color}`}
    >
      <Icon size={10} /> {status.replace("_", " ")}
    </span>
  );
}

export default function AdminSupport() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [drafts, setDrafts] = useState({}); // { [ticket_id]: replyText }
  const [submitting, setSubmitting] = useState({}); // { [ticket_id]: bool }

  const load = async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const query = status === "all" ? {} : { params: { status } };
      const { data } = await api.get("/admin/support/tickets", query);
      setTickets(data?.tickets || []);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error(e?.response?.data?.detail || "Could not load tickets");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const counts = useMemo(() => {
    const c = { open: 0, in_progress: 0, closed: 0 };
    tickets.forEach((t) => { c[t.status || "open"] = (c[t.status || "open"] || 0) + 1; });
    return c;
  }, [tickets]);

  const setDraft = (tid, val) => setDrafts((d) => ({ ...d, [tid]: val }));

  const sendReply = async (tid, close = false) => {
    const txt = (drafts[tid] || "").trim();
    if (txt.length < 5) { toast.error("Reply must be at least 5 characters"); return; }
    setSubmitting((s) => ({ ...s, [tid]: true }));
    const toastId = toast.loading(close ? "Replying + closing…" : "Sending reply…");
    try {
      const { data } = await api.post(`/admin/support/tickets/${tid}/reply`, {
        reply_text: txt, close,
      });
      setTickets((prev) => prev.map((t) => t.ticket_id === tid ? data.ticket : t));
      setDraft(tid, "");
      toast.success(close ? "Reply sent · ticket closed" : "Reply sent", { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply failed", { id: toastId });
    } finally {
      setSubmitting((s) => ({ ...s, [tid]: false }));
    }
  };

  const setStatusFor = async (tid, next) => {
    setSubmitting((s) => ({ ...s, [tid]: true }));
    const toastId = toast.loading(`Setting status → ${next}…`);
    try {
      await api.patch(`/admin/support/tickets/${tid}/status`, { status: next });
      setTickets((prev) => prev.map((t) => t.ticket_id === tid ? { ...t, status: next } : t));
      toast.success(`Status: ${next}`, { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed", { id: toastId });
    } finally {
      setSubmitting((s) => ({ ...s, [tid]: false }));
    }
  };

  return (
    <DashboardLayout>
      <div className="fade-up max-w-6xl" data-testid="admin-support-page">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-cyan-400 font-mono text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
              <ShieldAlert size={12} /> Internal Admin · Support Vault
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white flex items-center gap-3">
              <LifeBuoy size={26} className="text-cyan-300" /> Founder-Team Ticket Console
            </h1>
            <p className="text-slate-400 mt-2 text-sm">
              Review, reply, and resolve customer tickets. All actions are gated by the <span className="font-mono text-cyan-300">ADMIN_EMAILS</span> allowlist.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-support-refresh"
            className="h-10 px-3 rounded-lg glass hover:bg-white/10 text-slate-100 text-xs font-mono uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {forbidden && (
          <div
            data-testid="admin-support-forbidden"
            className="mb-6 rounded-lg p-4 border border-red-500/40 bg-red-500/5 text-red-200 text-sm"
          >
            <div className="font-semibold text-red-100">Admin allowlist required</div>
            <div className="mt-1 text-red-200/80">
              Signed in as <span className="font-mono">{user?.email}</span> — add this email to the backend
              <span className="font-mono"> ADMIN_EMAILS </span> env var and restart backend to unlock this console.
            </div>
          </div>
        )}

        {/* Filter toolbar */}
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="admin-support-filters">
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-400 flex items-center gap-1.5 mr-2">
            <Filter size={11} /> Status
          </div>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`admin-support-filter-${s}`}
              className={`px-3 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-widest border transition-colors ${status === s ? "bg-cyan-500/15 border-cyan-500/45 text-cyan-100" : "border-slate-500/30 text-slate-300 hover:border-slate-400/60 hover:text-white"}`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
          <div className="ml-auto text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-3">
            <span>Open · <span className="text-amber-300">{counts.open}</span></span>
            <span>In progress · <span className="text-cyan-300">{counts.in_progress}</span></span>
            <span>Closed · <span className="text-emerald-300">{counts.closed}</span></span>
          </div>
        </div>

        {/* Tickets */}
        {tickets.length === 0 && !loading && !forbidden ? (
          <div
            data-testid="admin-support-empty"
            className="subpage-glass-canvas p-10 text-center text-slate-400 text-sm"
          >
            No tickets in this queue. 🌿
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((t) => {
              const isOpen = expandedId === t.ticket_id;
              const snap = (t.asset_snapshots || [{}])[0] || {};
              return (
                <div
                  key={t.ticket_id}
                  data-testid={`admin-ticket-${t.ticket_id}`}
                  className="subpage-glass-canvas p-5"
                >
                  <button
                    onClick={() => setExpandedId(isOpen ? null : t.ticket_id)}
                    data-testid={`admin-ticket-toggle-${t.ticket_id}`}
                    className="w-full flex flex-wrap items-center gap-3 text-left"
                  >
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-md border ${CATEGORY_COLOR[t.category] || CATEGORY_COLOR.other}`}>
                      {t.category.replace("_", " ")}
                    </span>
                    <StatusPill status={t.status || "open"} />
                    <span className="text-white text-sm font-mono">{t.ticket_id}</span>
                    <span className="text-slate-300 text-sm truncate max-w-md">
                      {(t.message_body || "").slice(0, 90)}{(t.message_body || "").length > 90 ? "…" : ""}
                    </span>
                    <span className="ml-auto text-[11px] font-mono text-slate-500">
                      {t.email} · {(t.created_at || "").slice(0, 16)}
                    </span>
                    {isOpen ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                  </button>

                  {isOpen && (
                    <div className="mt-4 border-t border-white/5 pt-4 space-y-4" data-testid={`admin-ticket-body-${t.ticket_id}`}>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-500 mb-1">Message</div>
                        <div className="whitespace-pre-wrap text-slate-100 text-sm leading-relaxed bg-slate-950/40 rounded p-3 border border-white/5">
                          {t.message_body}
                        </div>
                      </div>
                      {snap && Object.keys(snap).length > 0 && (
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-cyan-300 mb-1">Reporter snapshot</div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono text-slate-300">
                            <div>Tier · <span className="text-white">{snap.subscription_tier || "—"}</span></div>
                            <div>Receipts · <span className="text-white">{snap.receipt_count ?? "—"}</span></div>
                            <div className="col-span-2 truncate">Path · <span className="text-white">{snap.path || "—"}</span></div>
                          </div>
                        </div>
                      )}
                      {(t.replies || []).length > 0 && (
                        <div>
                          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-emerald-300 mb-2 flex items-center gap-1.5">
                            <MailCheck size={11} /> Founder replies ({t.replies.length})
                          </div>
                          <div className="space-y-2">
                            {t.replies.map((rep) => (
                              <div
                                key={rep.reply_id}
                                data-testid={`admin-ticket-reply-${rep.reply_id}`}
                                className="rounded border border-emerald-500/20 bg-emerald-500/5 p-3"
                              >
                                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-1">
                                  {rep.author_email} · {(rep.created_at || "").slice(0, 16)}
                                </div>
                                <div className="text-slate-100 text-sm whitespace-pre-wrap">{rep.reply_text}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Reply composer */}
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-cyan-300 mb-1">Reply</div>
                        <textarea
                          value={drafts[t.ticket_id] || ""}
                          onChange={(e) => setDraft(t.ticket_id, e.target.value)}
                          rows={4}
                          maxLength={4000}
                          data-testid={`admin-ticket-reply-input-${t.ticket_id}`}
                          placeholder="Type your reply to the reporter…"
                          className="w-full rounded-lg bg-slate-900/70 border border-cyan-500/25 focus:border-cyan-400 outline-none text-white text-sm p-3 font-sans leading-relaxed"
                        />
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => sendReply(t.ticket_id, false)}
                            disabled={submitting[t.ticket_id]}
                            data-testid={`admin-ticket-reply-send-${t.ticket_id}`}
                            className="btn-neon h-10 px-4 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
                          >
                            <Send size={12} /> Send reply
                          </button>
                          <button
                            onClick={() => sendReply(t.ticket_id, true)}
                            disabled={submitting[t.ticket_id]}
                            data-testid={`admin-ticket-reply-close-${t.ticket_id}`}
                            className="h-10 px-4 rounded-lg border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-200 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
                          >
                            <CheckCircle2 size={12} /> Reply + close
                          </button>
                          <div className="ml-auto flex items-center gap-1.5">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Status:</span>
                            {["open", "in_progress", "closed"].map((s) => (
                              <button
                                key={s}
                                onClick={() => setStatusFor(t.ticket_id, s)}
                                disabled={submitting[t.ticket_id] || t.status === s}
                                data-testid={`admin-ticket-status-set-${s}-${t.ticket_id}`}
                                className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest border transition-colors ${
                                  t.status === s
                                    ? "border-cyan-500/45 bg-cyan-500/15 text-cyan-100"
                                    : "border-slate-500/30 text-slate-300 hover:text-white hover:border-slate-400/60"
                                }`}
                              >
                                {s.replace("_", " ")}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
